import { Box, Collapse, Grid, IconButton, makeStyles, Typography } from "@material-ui/core";
import PlanningItemHeader from "../../planning/components/planning-item-header";
import PlanningItemHeaderBadge from "../../planning/components/planning-item-header-badge";
import PlanningTimelineItem from "../../planning/components/planning-timeline-item";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FontAwesomeDuoToneStyle } from "../../../../fontawesome";
import PlanningItemBody from "../../planning/components/planning-item-body";
import PlanningItemContent from "../../planning/components/planning-item-content";
import DropType from "../../../../components/dropType/DropType";
import { ExternalStatusMultiList } from "../../../../components/external-status";
import { IconBasedContentBox } from "../../../../components/icon-based-content-box/icon-based-content-box";
import { Email } from "../../../../components/email";
import { PhoneNumber } from "../../../../components/phone-number";
import { Documents } from "../../../../components/documents";
import ArticlesManager from "../../job/articlesManager";
import { TextUtils } from "../../../../utils/text-utils";
import { ArticlesSummary } from "../../../../components/articles-summary";
import { useState } from "react";
import { KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { CustomerType } from "../../../../components/customer-type";
import { useEnumTranslations } from "../../../../hooks/use-enum-translations";
import { EnumTranslation } from "../../../../utils/enums/enum-translation";

const useStyle = makeStyles(theme => ({
    badgesWrapper: {
        display: 'flex',
        gap: theme.spacing(2)
    }
}));

const DropSummaryRemarks = ({ drop }) => {
    if (drop?.remark != null && drop.remark.trim !== '') {
        return (
            <IconBasedContentBox icon={<FontAwesomeIcon icon={["fad", "comment-alt-lines"]} size="lg" style={FontAwesomeDuoToneStyle} />}>
                <Box dangerouslySetInnerHTML={{ __html: drop.remark }} />
            </IconBasedContentBox>
        );
    }

    return null;
}

const ArticleSummaryOnlineData = ({ 
    drop 
}) => {
    const {t} = useTranslation();

    return (
        drop.onlineArticles != null && drop.onlineArticles.length > 0 ?
            <CollapsibleSummary title={t('Articles')}>
                <ArticlesSummary articles={drop.onlineArticles} />
            </CollapsibleSummary>  : null
    );

}

const CollapsibleSummary = ({ children, title }) => {
    const [collapsed, setCollapsed] = useState(true);

    return (
        <Grid container>
            <Grid item xs={12} container alignItems="center" justifyContent="space-between" onClick={() => setCollapsed(!collapsed)}>
                <Typography variant="h6">{title}</Typography>
                <IconButton display="flex" size="small">
                    {!collapsed ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </IconButton>                      
            </Grid>
            <Grid item xs={12}>     
                <Collapse in={!collapsed} timeout="auto" unmountOnExit>
                    <Box my={3}>
                        {children}
                    </Box>
                </Collapse>
            </Grid>
        </Grid>
    );

}

export const DropSummary = ({
    drop,
    isOnlineData
}) => {
    const classes = useStyle();
    const {t} = useTranslation();
    const [deliveryOrderStatusEnumTranslation] = useEnumTranslations(EnumTranslation.DeliveryOrderStatus, drop.deliveryOrderStatus);

    return (
        <PlanningTimelineItem icon={<FontAwesomeIcon icon={["far", "circle"]} />}>
            <PlanningItemHeader>
                <PlanningItemHeaderBadge>
                    <Typography component="span" style={{paddingLeft: 0}}>{drop.index + 1}</Typography>
                </PlanningItemHeaderBadge>
                <PlanningItemHeaderBadge>
                    <FontAwesomeIcon icon={["fad", "clock"]} style={FontAwesomeDuoToneStyle} />
                    <Typography component="span">
                        {drop.etaString} - {drop.etfString}
                    </Typography>
                </PlanningItemHeaderBadge>
                {
                    (drop != null && drop.colleagueSchedules != null && drop.colleagueSchedules.length > 0) ? 
                        drop.colleagueSchedules.map(schedule => (
                            <PlanningItemHeaderBadge key={`colleague-${schedule.deliveryOrderScheduleId}`}>
                                <FontAwesomeIcon icon={["fad", "user-hard-hat"]} style={FontAwesomeDuoToneStyle} /> 
                                <Typography component="span">{schedule.simulationSolutionRouteDrop?.driverName}</Typography>
                            </PlanningItemHeaderBadge>
                        )) : null
                }
                {
                    drop.deliveryOrderStatus != null ? 
                        <PlanningItemHeaderBadge>
                            <Typography style={{fontWeight: 'bold', textTransform: 'uppercase'}}>{deliveryOrderStatusEnumTranslation?.translatedTerm}</Typography>
                        </PlanningItemHeaderBadge> : 
                        null
                }
            </PlanningItemHeader>
            <PlanningItemBody>
                <PlanningItemContent>
                    <Box mx={5}>
                        <Typography variant="h6">{drop.location.name}</Typography>
                        <Typography variant="body1">{drop.location.address}</Typography>
                        <Typography variant="body1">{drop.location.remark}</Typography>
                        <Typography variant="body2">{drop.tw}</Typography>
                    </Box>
                    <Box m={5} mb={0} display="flex" flexDirection="column" alignItems="stretch" gridGap={15}>
                        {
                            drop.contactPerson != null && !TextUtils.isNullOrWhiteSpace([drop.contactPerson.lastName, drop.contactPerson.firstName].join(' ')) ? 
                                <IconBasedContentBox icon={<FontAwesomeIcon icon={["fad", "address-card"]} size="xl" style={FontAwesomeDuoToneStyle} />}>
                                    <Typography variant="body1">{[drop.contactPerson.lastName, drop.contactPerson.firstName].join(' ')}</Typography>
                                </IconBasedContentBox> : null
                        }
                        <Email value={drop.contactPerson?.email ?? drop.email} />
                        <PhoneNumber value={drop.contactPerson?.mobilePhone ?? drop.mobilePhone} />
                        {
                            drop?.jobInstruction != null && drop.jobInstruction.trim !== '' ? 
                                <IconBasedContentBox icon={<FontAwesomeIcon icon={["fad", "clipboard"]} size="lg" style={FontAwesomeDuoToneStyle} />}>
                                    <Box dangerouslySetInnerHTML={{ __html: drop.jobInstruction }} />
                                </IconBasedContentBox> : 
                                null
                        }
                        <DropSummaryRemarks drop={drop} />
                        {
                            isOnlineData ? 
                                <ArticleSummaryOnlineData drop={drop} /> : 
                                <ArticlesManager readonly dropId={drop.id} />
                        }
                        {
                            drop.documents != null && drop.documents.length > 0 ? 
                                <CollapsibleSummary title={`${t('Documents')} (${drop.documents.length})`}>
                                    <Documents documents={drop.documents} />
                                </CollapsibleSummary> : null
                        }
                    </Box>
                </PlanningItemContent>
            </PlanningItemBody>
            <Box mt={3} className={classes.badgesWrapper}>
                <CustomerType customerTypeId={drop.customerTypeId} />
                <DropType dropType={drop.dropType} />
                <ExternalStatusMultiList drop={drop} readOnly={true} />
            </Box>
        </PlanningTimelineItem>
    );

}